import { QueryConstraint, where } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import Sale from "../../database/entities/sales/sale.entity";
import ProductDB from "../../database/wrappers/product";
import SalesDB from "../../database/wrappers/sales";

interface ProductsPerHourFilter {
  estab: string
  store: string
  firstDate: string
  secondDate: string
  firstHour: string
  secondHour: string
}

function getDateTime(date: string, hour: string): Date {
  return new Date(`${date}T${hour || "00:00"}`);
}

function getWhere(dataFilter: ProductsPerHourFilter): QueryConstraint[] {
  return [
    where("created_at", ">=", getDateTime(dataFilter.firstDate, "00:00")),
    where("created_at", "<=", getDateTime(dataFilter.secondDate, "23:59:59.999")),
    dataFilter.store !== "#" && where("storeId", "==", dataFilter.store),
  ].filter(Boolean) as QueryConstraint[];
}

function getDate(value: any): Date | null {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (typeof value.seconds === "number") return new Date(value.seconds * 1000);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function getTimeInMinutes(value: string): number {
  const [hours = "0", minutes = "0"] = String(value || "00:00").split(":");
  return (Number(hours) * 60) + Number(minutes);
}

function adjustToReportTimezone(date: Date): number {
  const timeInMinutes = (date.getHours() * 60) + date.getMinutes();
  const adjustedTime = timeInMinutes - (3 * 60);
  return adjustedTime < 0 ? adjustedTime + (24 * 60) : adjustedTime;
}

function formatHourPeriod(timeInMinutes: number): string {
  const hour = Math.floor(timeInMinutes / 60);
  const formattedHour = String(hour).padStart(2, "0");
  const formattedNextHour = String((hour + 1) % 24).padStart(2, "0");
  return `${formattedHour}:00 às ${formattedNextHour}:00`;
}

function getProductTotal(product: any): number {
  return (product.qtd || 0) * (product.price || 0);
}

function hasValidName(name: any): boolean {
  return typeof name === "string" && name.trim().length > 0 && name.trim().toUpperCase() !== "N/I";
}

function getProductName(product: any, productsDicionary: Record<string, any>): string {
  if (hasValidName(product.name)) return product.name.trim();
  if (hasValidName(productsDicionary[product.id]?.name)) return productsDicionary[product.id].name.trim();
  return "Produto sem nome";
}

function useSalesProductsPerHour() {
  const [sales, setSales] = useState<Sale[]>([]);
  const [productsDicionary, setProductsDicionary] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [currentFilter, setCurrentFilter] = useState<ProductsPerHourFilter | null>(null);

  const getData = (dataFilter: ProductsPerHourFilter) => {
    if (!dataFilter.estab || !dataFilter.store) return;

    setLoading(true);
    setCurrentFilter(dataFilter);
    Promise.all([
      new SalesDB(dataFilter.estab).getAll(...getWhere(dataFilter)),
      new ProductDB(dataFilter.estab).getAll(),
    ])
      .then(([salesData, products]) => {
        const dicionary = products.reduce((obj: Record<string, any>, product: any) => {
          obj[product.id] = product;
          return obj;
        }, {});

        setSales(salesData);
        setProductsDicionary(dicionary);
      })
      .catch((error: any) => console.error(error))
      .finally(() => setLoading(false));
  };

  const productsPerHour = useMemo(() => {
    const rows: Record<string, any> = {};
    const startTime = getTimeInMinutes(currentFilter?.firstHour || "00:00");
    const endTime = getTimeInMinutes(currentFilter?.secondHour || "23:59");
    sales.forEach((sale) => {
      if (sale.reversed) return;

      const saleDate = getDate(sale.created_at);
      if (!saleDate) return;

      const saleTime = adjustToReportTimezone(saleDate);
      if (saleTime < startTime || saleTime > endTime) return;

      const period = formatHourPeriod(saleTime);

      (sale.products || []).forEach((product) => {
        const productId = product.id || product.name || "unknown";
        const name = getProductName(product, productsDicionary);
        const id = `${period}-${productId}`;

        if (!rows[id]) {
          rows[id] = {
            id,
            period,
            name,
            qtd: 0,
            total: 0,
          };
        }

        rows[id].qtd += product.qtd || 0;
        rows[id].total += getProductTotal(product);
      });
    });

    return Object.values(rows).sort((a: any, b: any) => (
      String(a.period || "").localeCompare(String(b.period || ""))
      || String(a.name || "").localeCompare(String(b.name || ""))
    ));
  }, [sales, currentFilter, productsDicionary]);

  useEffect(() => {
    setSales([]);
    setProductsDicionary({});
    setCurrentFilter(null);
  }, []);

  return { getData, loading, productsPerHour };
}

export default useSalesProductsPerHour;
